/* QC Trade catalog: collection filters/sort/view/pagination (Section Rendering API),
   card quick add (native /cart/add.js + Horizon cart events), PDP tabs, related products.
   Every feature is scoped to its own [data-qc-*] root and does nothing when that root is absent. */
import { CartLinesUpdateEvent } from '@shopify/events';

const VIEW_KEY = 'qc-coll-view';
const desktopMq = window.matchMedia('(min-width: 990px)');

/* ---------------- Collection ---------------- */
const qcT = (key, fallback) => (window.qcStrings && window.qcStrings[key]) || fallback;

function initCollection(root) {
  if (!root || root.dataset.qcReady === '1') return;
  root.dataset.qcReady = '1';
  const sectionId = root.dataset.sectionId;
  let controller = null;

  const applyView = (view) => {
    root.setAttribute('data-view', view);
    root.querySelectorAll('[data-qc-view]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.qcView === view)));
  };
  let saved = 'grid';
  try { saved = localStorage.getItem(VIEW_KEY) || 'grid'; } catch (e) {}
  applyView(saved === 'list' ? 'list' : 'grid');

  const announce = (msg) => {
    const live = root.querySelector('[data-qc-live]');
    if (live) live.textContent = msg;
  };

  const openDrawer = () => {
    const panel = root.querySelector('[data-qc-filters]');
    const scrim = root.querySelector('.qc-coll__scrim');
    if (!panel) return;
    panel.classList.add('is-open');
    if (scrim) scrim.hidden = false;
    document.documentElement.classList.add('qc-noscroll');
    root.querySelectorAll('[data-qc-drawer-open]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
    const first = panel.querySelector('.qc-filters__close');
    if (first) first.focus();
  };
  const closeDrawer = (restoreFocus = true) => {
    const panel = root.querySelector('[data-qc-filters]');
    const scrim = root.querySelector('.qc-coll__scrim');
    if (!panel || !panel.classList.contains('is-open')) return;
    panel.classList.remove('is-open');
    if (scrim) scrim.hidden = true;
    document.documentElement.classList.remove('qc-noscroll');
    const btn = root.querySelector('[data-qc-drawer-open]');
    if (btn) { btn.setAttribute('aria-expanded', 'false'); if (restoreFocus) btn.focus(); }
  };

  const buildUrlFromForm = (form) => {
    const action = new URL(form.getAttribute('action') || location.pathname, location.origin);
    const params = new URLSearchParams(action.search);
    new FormData(form).forEach((value, key) => {
      if (value === '' || value == null) return;
      params.append(key, value);
    });
    // keep the current sort when filtering
    const sort = root.querySelector('[data-qc-sort]');
    if (sort && form.matches('[data-qc-filter-form]')) params.set('sort_by', sort.value);
    // de-duplicate single-value keys
    ['q', 'sort_by'].forEach((k) => { const all = params.getAll(k); if (all.length > 1) params.set(k, all[all.length - 1]); });
    params.delete('page');
    const qs = params.toString();
    return action.pathname + (qs ? '?' + qs : '');
  };

  const render = async (url, { push = true, scroll = false } = {}) => {
    if (controller) controller.abort();
    controller = new AbortController();
    const fetchUrl = new URL(url, location.origin);
    fetchUrl.searchParams.set('section_id', sectionId);
    root.classList.add('is-loading');
    root.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch(fetchUrl.toString(), { signal: controller.signal, headers: { Accept: 'text/html' } });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-qc-coll]');
      if (!fresh) throw new Error('missing section');
      const wasOpen = !!root.querySelector('[data-qc-filters].is-open');
      const openGroups = Array.from(root.querySelectorAll('.qc-fgroup')).map((d) => d.open);
      const wrap = root.querySelector('.qc-page-wrap');
      const freshWrap = fresh.querySelector('.qc-page-wrap');
      if (wrap && freshWrap) wrap.replaceWith(freshWrap);
      root.classList.toggle('qc-coll--nofilters', fresh.classList.contains('qc-coll--nofilters'));
      // keep the user's open/closed groups and the drawer state
      root.querySelectorAll('.qc-fgroup').forEach((d, i) => { if (openGroups[i] !== undefined && !d.open) d.open = openGroups[i]; });
      if (wasOpen && !desktopMq.matches) {
        const panel = root.querySelector('[data-qc-filters]');
        const scrim = root.querySelector('.qc-coll__scrim');
        if (panel) panel.classList.add('is-open');
        if (scrim) scrim.hidden = false;
      }
      applyView(root.getAttribute('data-view') || 'grid');
      // keep the hero count in sync
      const heroCount = document.querySelector('.qc-chero__count strong');
      const freshCount = fresh.querySelector('.qc-toolbar__count strong:last-child');
      if (heroCount && freshCount) heroCount.textContent = freshCount.textContent;
      if (push) history.pushState({ qcColl: true }, '', url);
      const status = root.querySelector('.qc-toolbar__count');
      announce(status ? status.textContent.trim().replace(/\s+/g, ' ') : qcT('updated', 'Products updated'));
      if (scroll) {
        const top = root.getBoundingClientRect().top + window.scrollY - 140;
        if (window.scrollY > top) window.scrollTo({ top, behavior: 'smooth' });
      }
    } catch (err) {
      if (err && err.name === 'AbortError') return;
      location.assign(url);
    } finally {
      root.classList.remove('is-loading');
      root.removeAttribute('aria-busy');
    }
  };

  let debounce = null;
  root.addEventListener('change', (e) => {
    const t = e.target;
    if (t.matches('[data-qc-sort]')) {
      const mirror = root.querySelector('[data-qc-sort-mirror]');
      if (mirror) mirror.value = t.value;
      render(buildUrlFromForm(t.form), { scroll: false });
      return;
    }
    const form = t.closest('[data-qc-filter-form]');
    if (!form) return;
    // desktop: apply immediately; mobile drawer: wait for "Show results"
    if (!desktopMq.matches) return;
    clearTimeout(debounce);
    debounce = setTimeout(() => render(buildUrlFromForm(form)), t.type === 'number' ? 450 : 0);
  });

  root.addEventListener('submit', (e) => {
    const form = e.target;
    if (form.matches('[data-qc-filter-form]') || form.matches('[data-qc-sort-form]')) {
      e.preventDefault();
      const url = buildUrlFromForm(form);
      closeDrawer(false);
      render(url, { scroll: true });
    }
  });

  root.addEventListener('click', (e) => {
    const viewBtn = e.target.closest('[data-qc-view]');
    if (viewBtn) {
      const v = viewBtn.dataset.qcView;
      applyView(v);
      try { localStorage.setItem(VIEW_KEY, v); } catch (err) {}
      return;
    }
    if (e.target.closest('[data-qc-drawer-open]')) { openDrawer(); return; }
    if (e.target.closest('[data-qc-drawer-close]')) { closeDrawer(); return; }
    const more = e.target.closest('[data-qc-more]');
    if (more) {
      const group = more.closest('.qc-fgroup');
      const expanded = more.getAttribute('aria-expanded') === 'true';
      group.classList.toggle('is-expanded', !expanded);
      more.setAttribute('aria-expanded', String(!expanded));
      more.textContent = expanded ? more.dataset.label : qcT('showFewer', 'Show fewer');
      return;
    }
    const link = e.target.closest('a[data-qc-ajax]');
    if (link && !e.metaKey && !e.ctrlKey && !e.shiftKey && e.button === 0) {
      e.preventDefault();
      const isPager = !!link.closest('.qc-pager');
      closeDrawer(false);
      render(link.getAttribute('href'), { scroll: isPager });
    }
  });

  root.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeDrawer();
  });
  desktopMq.addEventListener('change', (e) => { if (e.matches) closeDrawer(false); });
  window.addEventListener('popstate', () => {
    if (document.body.contains(root)) render(location.pathname + location.search, { push: false });
  });
}

/* ---------------- Quick add (native cart endpoint) ---------------- */
function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
}

async function quickAdd(form) {
  const btn = form.querySelector('button[type="submit"]');
  const text = btn && btn.querySelector('.qc-cbtn__text');
  if (!btn || btn.disabled) return;
  btn.disabled = true;
  btn.classList.add('is-busy');
  if (text) text.textContent = qcT('adding', 'Adding…');
  const fd = new FormData(form);
  const id = String(fd.get('id'));
  const quantity = Number(fd.get('quantity') || 1);
  const sections = cartSectionIds();
  const deferred = CartLinesUpdateEvent.createPromise();
  form.dispatchEvent(new CartLinesUpdateEvent({
    action: 'add',
    context: 'product',
    lines: [{ merchandiseId: id, quantity }],
    promise: deferred.promise,
  }));
  try {
    const body = { items: [{ id: Number(id), quantity }] };
    if (sections.length) { body.sections = sections.join(','); body.sections_url = location.pathname; }
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || qcT('addError', 'Could not add to cart'));
    const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'qc-quick-add',
        sourceId: 'qc-quick-add',
        itemCount: quantity,
        sections: data.sections,
        didError: false,
      },
    });
    btn.classList.add('is-added');
    if (text) text.textContent = qcT('added', 'Added');
    setTimeout(() => {
      btn.classList.remove('is-added');
      if (text) text.textContent = btn.dataset.label || qcT('addToCart', 'Add to cart');
    }, 2200);
  } catch (err) {
    deferred.reject(err);
    btn.classList.add('is-error');
    if (text) text.textContent = qcT('tryAgain', 'Try again');
    const msg = form.closest('.qc-pcard')?.querySelector('.qc-pcard__title a')?.textContent?.trim();
    console.warn('[qc-quick-add]', msg || '', err && err.message);
    setTimeout(() => {
      btn.classList.remove('is-error');
      if (text) text.textContent = btn.dataset.label || qcT('addToCart', 'Add to cart');
    }, 2600);
  } finally {
    btn.disabled = false;
    btn.classList.remove('is-busy');
  }
}

document.addEventListener('submit', (e) => {
  const form = e.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('[data-qc-quick-add]')) return;
  e.preventDefault();
  quickAdd(form);
});

/* ---------------- PDP tabs ---------------- */
function initTabs(root) {
  if (!root || root.dataset.qcReady === '1') return;
  const list = root.querySelector('[data-qc-tablist]');
  if (!list) return;
  root.dataset.qcReady = '1';
  const tabs = Array.from(list.querySelectorAll('[role="tab"]'));
  const panels = tabs.map((t) => document.getElementById(t.getAttribute('aria-controls')));
  list.hidden = false;
  root.classList.add('is-enhanced');
  const select = (i, focus) => {
    tabs.forEach((t, j) => {
      const on = i === j;
      t.setAttribute('aria-selected', String(on));
      t.tabIndex = on ? 0 : -1;
      if (panels[j]) panels[j].hidden = !on;
    });
    if (focus) tabs[i].focus();
    // keep the active tab visible inside a horizontally scrolling tab bar (mobile) without moving the page
    const t = tabs[i];
    if (t.offsetLeft < list.scrollLeft || t.offsetLeft + t.offsetWidth > list.scrollLeft + list.clientWidth) {
      list.scrollLeft = t.offsetLeft - 16;
    }
  };
  select(0, false);
  list.addEventListener('click', (e) => {
    const t = e.target.closest('[role="tab"]');
    if (t) select(tabs.indexOf(t), false);
  });
  list.addEventListener('keydown', (e) => {
    const i = tabs.indexOf(document.activeElement);
    if (i < 0) return;
    let n = null;
    if (e.key === 'ArrowRight') n = (i + 1) % tabs.length;
    else if (e.key === 'ArrowLeft') n = (i - 1 + tabs.length) % tabs.length;
    else if (e.key === 'Home') n = 0;
    else if (e.key === 'End') n = tabs.length - 1;
    if (n !== null) { e.preventDefault(); select(n, true); }
  });
}

/* ---------------- Related products ---------------- */
async function initRelated(root) {
  if (!root || root.dataset.qcReady === '1' || !root.dataset.url) return;
  root.dataset.qcReady = '1';
  if (root.querySelector('.qc-grid')) { root.hidden = false; return; }
  try {
    const res = await fetch(root.dataset.url);
    if (!res.ok) return;
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    const grid = doc.querySelector('[data-qc-related] .qc-grid');
    if (!grid || !grid.children.length) return;
    root.querySelector('.qc-page-wrap').appendChild(grid);
    root.hidden = false;
  } catch (e) { /* recommendations are optional */ }
}

function init() {
  document.querySelectorAll('[data-qc-coll]').forEach(initCollection);
  document.querySelectorAll('[data-qc-tabs]').forEach(initTabs);
  document.querySelectorAll('[data-qc-related]').forEach(initRelated);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
document.addEventListener('shopify:section:load', init);
